import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { getDocumentCategoryLabel } from '@folio/utils/getDocumentCategoryLabel';
import { stopDraftResumePath } from '@folio/funnels/Stop/resume-step';
import { rejectedStopDocuments } from '@folio/funnels/Stop/stop-documents';
import { useStopDraft } from '@folio/funnels/Stop/useStopDraft';
import { useQuery } from '@tanstack/react-query';
import {
  commentsControllerListCommentsQueryOptions,
  configControllerGetPublicProductConfigQueryOptions,
  policyDocumentControllerGetPolicyDocumentConformityQueryOptions,
} from 'korint-sdk';
import { useFeatureFlag } from 'korint-sdk/react';
import { FileTextIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertTitle } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { formatZonedDateTime } from '../../lib/date-formatting';
import { StopAlertLayout, StopAlertMessage } from './StopAlertLayout';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

// A draft is not a scheduled termination: the policy is untouched, which this banner must say.
export function StopDraftAlert({
  funnelBasePath,
  className,
}: {
  funnelBasePath?: string;
  className?: string;
}) {
  // An app that never routed the funnel reads neither the flag nor the draft.
  return funnelBasePath ? (
    <DraftAlert funnelBasePath={funnelBasePath} className={className} />
  ) : null;
}

function DraftAlert({ funnelBasePath, className }: { funnelBasePath: string; className?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { policy } = usePolicyOnDashboard();
  const flagEnabled = useFeatureFlag('ops_policy_stop_branch', policy.productId);

  // A policy whose stop is scheduled or already executed can only answer "no draft".
  const { draft, discard, isDiscarding } = useStopDraft(policy.id, {
    enabled: flagEnabled && !policy.stop && policy.status !== 'POLICY_STOPPED',
  });

  const refusedBranchId = draft?.refusedBranchId;

  const { data: refusalReason } = useQuery({
    ...commentsControllerListCommentsQueryOptions(refusedBranchId!, { branchId: refusedBranchId! }),
    enabled: !!refusedBranchId,
    select: data => data?.data?.[0]?.comment,
  });

  const { data: conformity } = useQuery({
    ...policyDocumentControllerGetPolicyDocumentConformityQueryOptions(policy.id, {
      branchId: refusedBranchId!,
    }),
    enabled: !!refusedBranchId && !!draft?.requiredDocuments.length,
  });

  const { data: publicConfig } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
    enabled: !!refusedBranchId && !!draft?.requiredDocuments.length,
  });

  if (!draft) {
    return null;
  }

  const reason = t([
    `${policy.productId}.stopReasons.${draft.reason}`,
    `stopReasons.${draft.reason}`,
    draft.reason,
  ]);

  // Conformity is scoped to a branch and does not follow the fork, so the rejections stay on the
  // branch that was refused — a manager can reject a file and deny the request as a whole.
  const rejected = refusedBranchId
    ? rejectedStopDocuments({
        requiredCategories: draft.requiredDocuments,
        conformities: conformity?.conformities ?? [],
      })
    : [];

  return (
    <Alert className={className} variant={refusedBranchId ? 'destructive' : undefined}>
      <AlertTitle className="flex items-center justify-between gap-4 pt-1 grow">
        <StopAlertLayout icon={<FileTextIcon aria-hidden="true" className="mt-0.5 shrink-0" />}>
          {refusedBranchId ? (
            <>
              <span>
                {draft.stopDate
                  ? t('stopRefusal.bannerWithDate', {
                      reason,
                      date: formatZonedDateTime(new Date(draft.stopDate), policy.timeZone),
                    })
                  : t('stopRefusal.banner', { reason })}
              </span>
              {refusalReason && (
                <Text inline size="sm">
                  {t('stopRefusal.reason', { reason: refusalReason })}
                </Text>
              )}
              {rejected.map(({ documentId, category, reason: documentReason }) => (
                <Text inline size="sm" key={documentId}>
                  {t('stopRefusal.rejectedDocument', {
                    category: getDocumentCategoryLabel(
                      category,
                      publicConfig?.policyholderDocuments,
                      t,
                    ),
                    reason: documentReason,
                  })}
                </Text>
              ))}
            </>
          ) : (
            <StopAlertMessage
              lead={t('stopDraft.lead')}
              body={t('stopDraft.body')}
              /* A draft saved at step 1 has no date yet, so the date is a separate wording. */
              meta={
                draft.stopDate
                  ? t('stopDraft.meta', {
                      reason,
                      date: formatZonedDateTime(new Date(draft.stopDate), policy.timeZone),
                    })
                  : t('stopDraft.metaNoDate', { reason })
              }
            />
          )}
        </StopAlertLayout>
        <Group gap={2} className="shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              navigate(stopDraftResumePath({ funnelBasePath, policyId: policy.id, draft }))
            }
          >
            {t(refusedBranchId ? 'stopRefusal.resume' : 'stopDraft.resume')}
          </Button>
          <Button
            variant="destructive-ghost"
            size="sm"
            loading={isDiscarding}
            aria-label={t('stopDraft.discard')}
            onClick={() => discard(draft.branchId)}
          >
            <Trash2Icon className="h-4 w-4" />
          </Button>
        </Group>
      </AlertTitle>
    </Alert>
  );
}
